import { useState } from 'react';
import { Link } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';
import SEOJsonLd from '@/components/base/SEOJsonLd';

const introPoints = [
  'Founded in Taiwan, deeply rooted in the health supplement OEM/ODM industry',
  'Proprietary GMP-certified facilities meeting international manufacturing standards',
  'Integrated R&D, production, and quality management services',
  'Serving over 250 brand clients across diverse markets',
  'Multiple dosage forms available to meet diverse market demands',
];

const brandValues = [
  { icon: 'ri-heart-pulse-line', title: 'Health First', desc: 'Consumer health is our core value — we rigorously oversee every step of the manufacturing process.' },
  { icon: 'ri-lightbulb-line', title: 'Continuous Innovation', desc: 'We track global biotech trends and continuously introduce new ingredients and technologies.' },
  { icon: 'ri-shield-check-line', title: 'Quality Commitment', desc: 'Every batch comes with a complete inspection report, ensuring consistent product quality.' },
  { icon: 'ri-group-line', title: 'Partnership Spirit', desc: 'We treat clients as long-term partners, offering advisory support that goes beyond manufacturing.' },
];

const stats = [
  { value: '1600+', label: 'Formula Database', icon: 'ri-capsule-line' },
  { value: '70%', label: 'Repeat Order Rate', icon: 'ri-award-line' },
  { value: '250+', label: 'Brand Partners', icon: 'ri-test-tube-line' },
  { value: '7', label: 'Dosage Form Types', icon: 'ri-building-line' },
];

const expertiseBlocks = [
  {
    tag: 'R&D Capability',
    title: 'In-House R&D, Formula Innovation',
    desc: 'Kang Yong Biotech operates its own R&D laboratory, staffed with professionals in food science, nutrition, and biochemistry. We continuously track international ingredient trends to develop unique, differentiated formulas for our clients.',
    points: [
      'PhD and Master-level researchers in food science and biotech',
      'In-house analytical instruments for formula stability testing',
      'Regular participation in international ingredient expos',
      'NDA confidentiality agreements available upon request',
    ],
    img: 'https://readdy.ai/api/search-image?query=Asian%20female%20scientist%20in%20white%20lab%20coat%20carefully%20pipetting%20herbal%20extract%20liquid%20into%20test%20tubes%20in%20a%20bright%20modern%20biotechnology%20research%20laboratory%2C%20rows%20of%20green%20plant%20samples%20on%20workbench%2C%20warm%20natural%20daylight%20streaming%20through%20window%2C%20shallow%20depth%20of%20field&width=720&height=500&seq=expertise-rd-02&orientation=landscape',
    imgLeft: false,
  },
  {
    tag: 'Manufacturing',
    title: 'GMP Facility, Strict Process Control',
    desc: "Kang Yong's facility holds GMP certification with production environments meeting pharmaceutical-grade standards. From raw material intake to finished product shipment, every step follows a complete quality management process to ensure safety and batch consistency.",
    points: [
      'GMP-compliant cleanroom production environment',
      'Full temperature and humidity monitoring throughout production',
      'Multiple dosage form lines: capsules, tablets, powder packs, beverages',
      'Complete batch inspection reports issued for every production run',
    ],
    img: 'https://readdy.ai/api/search-image?query=modern%20GMP%20certified%20pharmaceutical%20manufacturing%20facility%20production%20line%20with%20stainless%20steel%20equipment%20workers%20in%20white%20uniforms%20hairnets%2C%20clean%20room%20environment%2C%20health%20supplement%20factory%20interior&width=720&height=500&seq=expertise-mfg-01&orientation=landscape',
    imgLeft: true,
  },
  {
    tag: 'Quality Management',
    title: 'End-to-End Quality Control for Safe Market Entry',
    desc: 'We have established a comprehensive quality management system covering raw material acceptance, in-process control, and finished product testing. Every step follows standard operating procedures to ensure compliance with regulations in Taiwan and export markets.',
    points: [
      'Supplier auditing and periodic inspection protocols',
      'Finished product testing: microbiology, heavy metals, safety indicators',
      'Support for health food certification applications and label review',
      'Complete batch traceability system for full product recall support',
    ],
    img: 'https://readdy.ai/api/search-image?query=quality%20control%20inspector%20testing%20health%20supplement%20products%20in%20laboratory%20holding%20clipboard%20checking%20bottles%20tablets%20capsules%2C%20professional%20QC%20process%20in%20biotech%20facility%2C%20clean%20bright%20environment&width=720&height=500&seq=expertise-qc-01&orientation=landscape',
    imgLeft: false,
  },
];

const facilities = [
  {
    title: 'R&D Laboratory',
    desc: 'Equipped with modern analytical instruments supporting formula development and stability testing',
    img: 'https://static.readdy.ai/image/c58d2dc5fdc4f4caa90264537c733184/61ccb92d659090df2e345c00bd601e46.jpeg',
  },
  {
    title: 'Cleanroom Production',
    desc: 'Class 10,000 cleanroom meeting GMP-compliant production standards',
    img: 'https://readdy.ai/api/search-image?query=pharmaceutical%20clean%20room%20production%20facility%20workers%20in%20white%20protective%20suits%20hairnets%20working%20on%20assembly%20line%2C%20GMP%20certified%20manufacturing%20environment%2C%20stainless%20steel%20equipment&width=600&height=420&seq=facility-clean-01&orientation=landscape',
  },
  {
    title: 'QC Testing Lab',
    desc: 'Comprehensive testing equipment with certified inspection reports issued per batch',
    img: 'https://readdy.ai/api/search-image?query=quality%20control%20laboratory%20technician%20testing%20supplement%20product%20samples%20with%20professional%20instruments%2C%20clean%20bright%20lab%20environment%2C%20health%20food%20safety%20testing%20facility&width=600&height=420&seq=facility-qc-01&orientation=landscape',
  },
  {
    title: 'Warehouse & Logistics',
    desc: 'Temperature-controlled storage management ensuring safe storage of materials and finished goods',
    img: 'https://readdy.ai/api/search-image?query=modern%20pharmaceutical%20warehouse%20storage%20facility%20with%20organized%20shelves%20products%20boxes%20temperature%20controlled%20environment%2C%20professional%20logistics%20management%2C%20clean%20orderly%20storage&width=600&height=420&seq=facility-warehouse-01&orientation=landscape',
  },
];

const cases = [
  {
    category: 'Capsule Form',
    title: 'Compound Botanical Capsule Series',
    desc: 'Combining multiple natural plant extracts with customized formulas targeting health maintenance — highly popular with e-commerce brands.',
    img: 'https://readdy.ai/api/search-image?query=natural%20herb%20botanical%20capsule%20supplement%20product%20with%20green%20plants%20ingredients%20on%20clean%20white%20marble%20background%2C%20professional%20health%20product%20photography%2C%20elegant%20minimal%20packaging&width=600&height=400&seq=case-capsule-01&orientation=landscape',
    tags: ['Capsule', 'Botanical Extract', 'Custom Formula'],
  },
  {
    category: 'Pet Health',
    title: 'Functional Pet Health Series',
    desc: 'Developed for the pet health market and compliant with feed additive regulations, offering pet-specific functional health formulas.',
    img: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20product%20cat%20dog%20natural%20organic%20treats%20vitamins%20on%20light%20background%2C%20cute%20packaging%20pet%20care%20product%20photography%2C%20clean%20minimal%20style&width=600&height=400&seq=case-pet-01&orientation=landscape',
    tags: ['Pet Health', 'Functional Formula', 'Compliant'],
  },
  {
    category: 'Beverage Form',
    title: 'Functional Beverage OEM Series',
    desc: 'Mature liquid dosage form production technology, from collagen drinks to functional beverages, helping clients build bestselling product lines.',
    img: 'https://readdy.ai/api/search-image?query=functional%20health%20drink%20bottle%20supplement%20beverage%20product%20display%20on%20clean%20light%20background%2C%20collagen%20protein%20drink%20packaging%2C%20professional%20product%20photography%20with%20natural%20green%20elements&width=600&height=400&seq=case-drink-01&orientation=landscape',
    tags: ['Beverage', 'Collagen', 'Functional Drink'],
  },
];

const reasons = [
  { icon: 'ri-flask-line', title: 'In-House R&D Lab', desc: 'Professional R&D team providing full technical support, from ingredient selection to formula design.' },
  { icon: 'ri-verified-badge-line', title: 'GMP Certified Facility', desc: 'Meets international GMP standards — a safe, reliable, and consistent production environment.' },
  { icon: 'ri-file-list-3-line', title: 'Regulatory Compliance', desc: 'Familiar with Taiwan and international health supplement regulations, assisting with product labeling review and regulatory submissions for compliant market entry.' },
  { icon: 'ri-time-line', title: 'Fast Lead Times', desc: 'Well-organized production scheduling effectively shortens the timeline from sample to mass production.' },
  { icon: 'ri-customer-service-2-line', title: 'Dedicated Account Service', desc: 'One-on-one account managers accompany you from consultation through delivery.' },
  { icon: 'ri-leaf-line', title: 'Natural Ingredients First', desc: 'Carefully selected premium natural ingredients, ensuring ingredient sourcing aligns with market trends.' },
];

const subPages = [
  { title: 'Core Advantages', desc: 'GMP certification, R&D expertise, and complete regulatory support that set Kang Yong apart.', href: '/en/about/core-advantages', icon: 'ri-medal-line' },
  { title: 'Professional Production', desc: 'Our GMP-certified production facility and quality management systems.', href: '/en/about/production', icon: 'ri-building-2-line' },
  { title: 'Success Stories', desc: 'Real brand collaboration outcomes — from concept to market-ready products.', href: '/en/about/cases', icon: 'ri-trophy-line' },
  { title: 'Regulatory Risk Control', desc: 'Health supplement labeling compliance and regulatory risk management.', href: '/en/about/regulation', icon: 'ri-file-shield-2-line' },
];

export default function EnAboutPage() {
  const [_open] = useState(false);

  const siteUrl = (window as any).__VITE_SITE_URL__ || 'https://kangyong.com';
  useSEO({
    title: 'About Kang Yong Biotech | GMP OEM/ODM Manufacturer Taiwan',
    description: 'Learn about Kang Yong Biotech — a GMP-certified health supplement OEM/ODM manufacturer in Taiwan with in-house R&D, quality management, and multi-country export experience.',
    keywords: 'Kang Yong Biotech, GMP manufacturer Taiwan, health supplement OEM about, biotech company Taiwan',
    lang: 'en',
    canonical: 'https://kangyong.com/en/about',
  });

  return (
    <EnLayout>
      <SEOJsonLd type="WebPage" pageTitle="About Kang Yong Biotech | GMP OEM/ODM Manufacturer Taiwan" pageDescription="Learn about Kang Yong Biotech — a GMP-certified health supplement OEM/ODM manufacturer in Taiwan with in-house R&D, quality management, and multi-country export experience." pageUrl={`${siteUrl}/en/about`} />
      {/* Hero */}
      <section className="relative pt-20 min-h-[420px] md:min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20biotech%20pharmaceutical%20factory%20interior%20with%20clean%20production%20line%20equipment%20stainless%20steel%20tanks%20workers%20in%20white%20uniforms%2C%20bright%20professional%20industrial%20environment%2C%20health%20supplement%20manufacturing%20facility&width=1920&height=480&seq=about-hero-bg-01&orientation=landscape"
            alt="About Kang Yong Biotech"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/85 via-[#1a3a1a]/60 to-[#1a3a1a]/30" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-16 pt-24">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 tracking-wide">
              More Than a Manufacturer
            </span>
            <h1 className="text-white font-bold leading-tight mb-5">
              <span className="block">Your Trusted Biotech</span>
              <span className="block text-green-300">Innovation Partner</span>
            </h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl mb-8">
              Kang Yong Biotech combines professional R&D capabilities with rigorous quality management to guide brands from concept to market — your most reliable long-term partner in health supplement manufacturing.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/en/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                Request Consultation
              </Link>
              <Link to="/en/services" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                View Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              About Kang Yong
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">
              Science-Based, Quality-Committed
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Kang Yong Biotech is a professional health supplement ODM/OEM manufacturer based in Taiwan, combining R&D strength with manufacturing expertise to be the most reliable partner for brand growth.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16 mb-20">
            <div className="w-full lg:w-1/2">
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                Since its founding, Kang Yong Biotech has upheld "Professionalism, Quality, and Trust" as its core values, committed to providing the most comprehensive OEM/ODM services to health supplement brands. Our services cover formula R&D, raw material sourcing, production, quality management, and packaging — so clients can focus on brand development while leaving everything else to us.
              </p>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-8">
                Over the years, we have served startups, pharmacy chains, e-commerce platforms, and international exporters, accumulating rich manufacturing experience and market insights — making us the ideal starting point for entering the health supplement market.
              </p>
              <ul className="space-y-3">
                {introPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                    </div>
                    <span className="text-[#1a2e1a] text-[0.9375rem] leading-[1.75]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=team%20of%20professional%20biotech%20scientists%20researchers%20discussing%20in%20modern%20laboratory%20with%20plants%20herbs%20ingredients%20on%20table%2C%20collaborative%20meeting%20environment%2C%20natural%20lighting%2C%20health%20supplement%20company%20team&width=720&height=500&seq=about-company-01&orientation=landscape"
                  alt="Kang Yong Biotech Team"
                  className="w-full h-[360px] lg:h-[420px] object-cover object-top rounded-2xl"
                />
                <div className="absolute bottom-5 right-5 bg-white/95 rounded-xl px-5 py-3 flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
                    <i className="ri-building-2-line text-[#4CAF50] text-lg" />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">Years of OEM Experience</p>
                    <p className="text-gray-400 text-[0.75rem]">Deeply rooted in Taiwan's biotech industry</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {brandValues.map((v) => (
              <div key={v.title} className="bg-[#f7faf7] rounded-2xl p-6 text-center hover:border-green-200 border border-transparent hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-xl mx-auto mb-4 border border-green-100">
                  <i className={`${v.icon} text-[#4CAF50] text-2xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{v.title}</h3>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Professional Background
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">
              R&amp;D × Manufacturing × Quality<br className="hidden sm:block" />A Fully Integrated Expertise
            </h2>
          </div>
          <div className="space-y-16">
            {expertiseBlocks.map((block) => (
              <div
                key={block.title}
                className={`flex flex-col ${block.imgLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-10 lg:gap-16 items-center`}
              >
                <div className="w-full lg:w-1/2">
                  <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.8125rem] font-medium px-3 py-1 rounded-full mb-4">
                    {block.tag}
                  </span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-4">{block.title}</h3>
                  <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{block.desc}</p>
                  <ul className="space-y-3">
                    {block.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                        </div>
                        <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="w-full lg:w-1/2 flex-shrink-0">
                  <div className="rounded-2xl overflow-hidden">
                    <img src={block.img} alt={block.title} className="w-full h-[320px] lg:h-[380px] object-cover object-top rounded-2xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Our Numbers
            </span>
            <h2 className="text-[#1a2e1a] font-bold">Our Capabilities in Numbers</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s) => (
              <div key={s.label} className="bg-[#f7faf7] rounded-2xl p-8 text-center border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-4">
                  <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                </div>
                <p className="text-[#1a2e1a] font-bold text-[2.125rem] leading-none mb-2">{s.value}</p>
                <p className="text-gray-500 text-[0.9375rem]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GMP Facility */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Our Facilities
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">
              GMP-Standard Facility, Full Production Assurance
            </h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Kang Yong Biotech's facility holds multiple international certifications, providing the highest-standard production environment from R&D to shipment.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {facilities.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 group">
                <div className="w-full h-[240px] overflow-hidden">
                  <img src={f.img} alt={f.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-5">
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { num: '1600+', label: 'Formula Database' },
              { num: '70%', label: 'Repeat Order Rate' },
              { num: '250+', label: 'Brand Partners' },
              { num: '7', label: 'Dosage Form Types' },
            ].map((item) => (
              <div key={item.label} className="bg-[#4CAF50] rounded-xl py-6 text-center">
                <p className="text-white font-bold text-[2rem] leading-none mb-1">{item.num}</p>
                <p className="text-green-100 text-[0.875rem]">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">
              Manufacturing Cases
            </span>
            <h2 className="text-[#1a2e1a] font-bold mb-4">Diverse Products, Rich Case Portfolio</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
              Kang Yong Biotech serves a wide range of product categories — below are selected representative manufacturing cases.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cases.map((c) => (
              <div key={c.title} className="bg-[#f7faf7] rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 transition-all duration-300 group cursor-default">
                <div className="w-full h-[220px] overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <span className="inline-block bg-green-100 text-[#4CAF50] text-[0.75rem] font-medium px-3 py-1 rounded-full mb-3">{c.category}</span>
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{c.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-4">{c.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {c.tags.map((tag) => (
                      <span key={tag} className="bg-white border border-gray-200 text-gray-500 text-[0.75rem] px-3 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/en/services" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1rem] px-8 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
              View More Services <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
            <div className="w-full lg:w-2/5 flex-shrink-0">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">
                Why Kang Yong
              </span>
              <h2 className="text-[#1a2e1a] font-bold leading-tight mb-6">
                Why Do 250+ Brands<br />Choose Us?
              </h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] mb-8">
                We're not just a manufacturer — we're your strategic partner in brand success. From product development to market launch, Kang Yong provides full professional support every step of the way, backed by a 70% repeat order rate and 250+ trusted partnerships.
              </p>
              <Link
                to="/en/contact/form"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[1rem] px-7 py-4 rounded-full transition-all duration-300 cursor-pointer"
              >
                Contact Us Now <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="w-full lg:w-3/5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {reasons.map((r) => (
                  <div key={r.title} className="bg-white rounded-xl p-5 flex gap-4 border border-gray-100 hover:border-green-200 transition-all duration-300">
                    <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0">
                      <i className={`${r.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <div>
                      <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1">{r.title}</h3>
                      <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sub-page nav */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-[#1a2e1a] font-bold text-[2rem] md:text-[2.5rem] mb-4">Learn More About Us</h2>
            <p className="text-gray-500 text-[1.0625rem] max-w-2xl mx-auto">
              Explore our core advantages, production capabilities, success stories and regulatory expertise.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {subPages.map((page) => (
              <Link
                key={page.href}
                to={page.href}
                className="flex items-start gap-5 bg-[#f7faf7] rounded-2xl p-7 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 flex-shrink-0 group-hover:bg-[#4CAF50] transition-colors duration-300">
                  <i className={`${page.icon} text-[#4CAF50] text-xl group-hover:text-white transition-colors duration-300`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{page.title}</h3>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.7]">{page.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[#4CAF50] text-[0.875rem] font-medium mt-3">
                    Learn more <i className="ri-arrow-right-line text-xs" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=vibrant%20lush%20green%20tropical%20botanical%20garden%20with%20sunlight%20filtering%20through%20leaves%2C%20rich%20emerald%20green%20foliage%20background%2C%20health%20nature%20wellness%20theme%2C%20beautiful%20natural%20scene&width=1920&height=600&seq=about-cta-bg-01&orientation=landscape"
            alt="CTA Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#1a3a1a]/82" />
        </div>
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-6">
            Start Partnership
          </span>
          <h2 className="text-white font-bold leading-tight mb-6">
            Ready to Make Kang Yong<br />Your Manufacturing Partner?
          </h2>
          <p className="text-green-200 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto mb-10">
            Whether you're a startup brand or an established enterprise, Kang Yong Biotech can provide the most suitable OEM/ODM solution for your needs. Contact our business team today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/en/contact/form" className="whitespace-nowrap bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              Request Consultation
            </Link>
            <Link to="/en/partnership" className="whitespace-nowrap bg-white/15 hover:bg-white/25 border border-white/60 text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              View Partnership Process
            </Link>
          </div>
        </div>
      </section>
    </EnLayout>
  );
}
